import ScreenedAndReportedResponseAssessmentCell from '@app/src/components/Table/Cells/AssessmentResultCell'
import CheckboxCell from '@app/src/components/Table/Cells/CheckboxCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import DataFoundCell from '@app/src/components/Table/Cells/DataFoundCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import ExternalVerificationStatusCell from '@app/src/components/Table/Cells/ExternalVerificationStatusCell'
import PeriodNameCell from '@app/src/components/Table/Cells/PeriodNameCell'
import RequestCell from '@app/src/components/Table/Cells/RequestCell'
import ScrapingFailedCell from '@app/src/components/Table/Cells/ScrapingFailedCell'
import ScrapingInProgressCell from '@app/src/components/Table/Cells/ScrapingInProgressCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { useAccount } from '@app/src/context/AccountContext'
import { useCompleteAutomationSummary } from '@app/src/hooks/useAutomationSummary'
import type { ResponseScraping } from '@app/src/types/resourceExplorer'
import {
  ExternalVerificationStatus,
  ResponseDraftStatus,
  ResponseScrapingStatus,
} from '@app/src/types/resourceExplorer'
import { getAutomationTooltip } from '@app/src/utils/automationTooltipHelper'
import { reporting } from '@app/src/wf-constants/paths'
import { TableRow } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'

interface ScrapedResponseRow {
  row: ResponseScraping
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>, row: ResponseScraping) => void
  selectedResponsesIds?: Array<number>
}

export const AccessorScrapedResponseRow: React.FC<ScrapedResponseRow> = ({
  row: responseScraping,
  selectedResponsesIds,
  onCheckboxChange,
}) => {
  const history = useHistory()
  const { account } = useAccount()
  const { formatMessage } = useIntl()

  const inquiry = responseScraping?.response?.request?.inquiries?.find(
    inquiry => inquiry.creatorOrganizationId === account?.organization?.id,
  )

  const isChecked = Boolean(selectedResponsesIds?.find(responseId => responseId === responseScraping.id))
  const scrapingFailed = responseScraping.status === ResponseScrapingStatus.Failed

  // Compute automation tooltip
  // NOTE: This hook is called per row, BUT React Query caches API calls by automation ID
  // So if 100 rows have the same automation, the API is called only once
  const automation = responseScraping?.requestAutomation
  const automationSummary = useCompleteAutomationSummary(automation)
  const automationTooltip = automation ? getAutomationTooltip(automation, automationSummary, formatMessage) : undefined
  const assessment = responseScraping.provider.assessments?.find(
    assessment =>
      assessment.periodName === responseScraping.periodName &&
      assessment.assessmentTemplate.questionnaireTemplateId === responseScraping.questionnaireId &&
      responseScraping.percentageFound != null,
  )

  const noPublicData = responseScraping.percentageFound === 0

  return (
    <TableRow selected={isChecked}>
      {onCheckboxChange && <CheckboxCell checked={isChecked} onChange={e => onCheckboxChange(e, responseScraping)} />}
      {responseScraping.status !== ResponseScrapingStatus.Completed ? (
        <ExternalVerificationStatusCell draftStatus={ResponseDraftStatus.Scraped} />
      ) : (
        <ExternalVerificationStatusCell
          responseStatus={
            responseScraping?.response?.verifications?.[0]?.externalVerificationStatus ??
            ExternalVerificationStatus.NotSet
          }
          draftStatus={responseScraping?.response?.draftStatus}
          inquiryStatus={inquiry?.status}
        />
      )}
      <RequestCell
        title={responseScraping.questionnaireTemplate?.title}
        imageUrl={responseScraping.questionnaireTemplate?.image?.url}
        isScraped
        disabled={scrapingFailed}
        {...(responseScraping.status === ResponseScrapingStatus.Completed
          ? {
              onClick: () =>
                history.push({
                  pathname: reporting,
                  search: `requestId=${responseScraping.response?.request?.id}&responseId=${responseScraping.response?.id}`,
                }),
            }
          : {})}
      />
      <PeriodNameCell periodName={responseScraping.periodName} disabled={scrapingFailed} />
      <CompanyCell company={responseScraping.provider} drawer />
      {responseScraping.status === ResponseScrapingStatus.InProgress ? (
        <ScrapingInProgressCell />
      ) : scrapingFailed ? (
        <ScrapingFailedCell />
      ) : (
        <DataFoundCell
          dataFound={responseScraping.percentageFound ?? 0}
          link={{
            pathname: reporting,
            search: `requestId=${responseScraping.response?.request?.id}&responseId=${responseScraping.response?.id}`,
          }}
        />
      )}
      <ScreenedAndReportedResponseAssessmentCell
        assessment={assessment}
        noPublicData={noPublicData}
        hideScore={!responseScraping.response?.isLatestSubmitted}
      />
      <DateCell value={responseScraping?.startedAt} automationTooltip={automationTooltip} disabled={scrapingFailed} />
      <ValueCell value={formatMessage({ id: 'general.aiScreening' })} disabled={scrapingFailed} />
    </TableRow>
  )
}

export default AccessorScrapedResponseRow
